/**
 * AI tool-toggles admin API client (Phase X.3.7c C2).
 *
 * Wraps the two endpoints under /v1/admin-review/tool-toggles/. Owner/admin-
 * gated server-side (manage_team); the rail visibility is convenience, not
 * security. Mirrors src/lib/aiCaps.ts.
 *
 * Also the one read of which tools the tenant has switched off
 * (useDisabledAiTools), shared by the AI tools landing and every photo door
 * (usePhotoIntakeDoor): one query key, so a page with several doors reads
 * once. The read fails open: a tool shows while it loads or after it fails,
 * and hides only when a completed read says it is off. Each handler's 403
 * is the authoritative gate.
 *
 * Also the slug<->key reconciliation point: the portal uses the slug
 * `brand-formatter` (hyphen) in routes; the canonical tool_key is the underscore
 * form `brand_formatter` (matches ai_drafts.tool_name). slugToToolKey maps it.
 */

import { writeFetch } from './writeFetch';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AiToolSlug } from '@/pages/AiTools';
import { apiErrorFrom } from './api';

export type ToolKey =
  | 'email'
  | 'assessment'
  | 'assessment_letter'
  | 'brand_formatter'
  | 'intake'
  | 'photo_intake'
  | 'med_list';

export interface ToolState {
  tool_key: ToolKey;
  enabled: boolean;
}

export interface ToolTogglesResponse {
  tools: ToolState[];
  request_id?: string;
}

/** Map a portal slug to its canonical tool_key (the hyphen<->underscore forms
 *  differ for brand-formatter and med-list). */
export function slugToToolKey(slug: AiToolSlug): ToolKey {
  if (slug === 'brand-formatter') return 'brand_formatter';
  if (slug === 'assessment-letter') return 'assessment_letter';
  if (slug === 'med-list') return 'med_list';
  return slug;
}

/** Friendly labels for the admin toggle list (keyed by canonical tool_key). */
export const TOOL_LABELS: Record<ToolKey, string> = {
  email: 'Email Helper',
  assessment: 'Formal Assessment Report',
  assessment_letter: 'Summary Assessment Letter',
  brand_formatter: 'Brand Formatter',
  intake: 'Intake Summary Generator',
  photo_intake: 'Add from a Photo',
  med_list: 'Medication List',
};

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

const base = (slug: string): string =>
  `/api/t/${encodeURIComponent(slug)}/v1/admin-review/tool-toggles`;

export const toolTogglesApi = {
  /** Admin list (manage_team) — backs the Limits & access tab. */
  async list(tenantSlug: string, signal?: AbortSignal): Promise<ToolTogglesResponse> {
    return (await requestJson(base(tenantSlug), { signal })) as ToolTogglesResponse;
  },

  /** Portal read (use_ai_tools) — backs the landing filter for non-admin CMs. */
  async listForPortal(tenantSlug: string, signal?: AbortSignal): Promise<ToolTogglesResponse> {
    return (await requestJson(
      `/api/t/${encodeURIComponent(tenantSlug)}/v1/ai-tools/enabled`,
      { signal },
    )) as ToolTogglesResponse;
  },

  async setToggle(
    tenantSlug: string,
    toolKey: ToolKey,
    enabled: boolean,
  ): Promise<{ saved: true }> {
    return (await requestJson(`${base(tenantSlug)}/${encodeURIComponent(toolKey)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    })) as { saved: true };
  },
};

/** The cache key of the tenant's tool on/off read (the portal endpoint). */
export function aiToolsEnabledQueryKey(tenantSlug: string): readonly unknown[] {
  return ['ai-tools', 'enabled', tenantSlug];
}

/**
 * The tools the tenant has switched off, from the portal read. Empty while
 * the read loads, after it fails, or when `enabled` is false (then nothing
 * is fetched). Held five minutes and never retried on a remount, so a tab
 * switch or a remounted door adds no fetch while the entry lives; the
 * admin switch invalidates the key, so the admin's own screens update at
 * once and everyone else within five minutes or on reload.
 */
export function useDisabledAiTools(tenantSlug: string, enabled: boolean): ReadonlySet<ToolKey> {
  const query = useQuery({
    queryKey: aiToolsEnabledQueryKey(tenantSlug),
    queryFn: ({ signal }) => toolTogglesApi.listForPortal(tenantSlug, signal),
    staleTime: 5 * 60_000,
    retry: false,
    retryOnMount: false,
    enabled: enabled && tenantSlug.length > 0,
  });
  const tools = query.data?.tools;
  return useMemo(
    () => new Set((tools ?? []).filter((t) => !t.enabled).map((t) => t.tool_key)),
    [tools],
  );
}

/**
 * Whether a photo door shows: the caller's own gate (`eligible`), and the
 * tenant has not switched the photo reader off. Shows while the read loads
 * or after it fails; never fetches when `eligible` is false.
 */
export function usePhotoIntakeDoor(tenantSlug: string, eligible: boolean): boolean {
  const disabled = useDisabledAiTools(tenantSlug, eligible);
  return eligible && !disabled.has('photo_intake');
}
